import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, FileText, Activity, UserPlus, Shield, Mail, Globe } from 'lucide-react';

export const DashboardStats = ({ dashboardData, languageDemographics }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Total Patients</CardTitle>
          <Users className="h-6 w-6 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{dashboardData?.stats?.patientCount || 0}</div>
          <p className="text-sm text-gray-600 mt-1">Registered patients</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Procedures Delivered</CardTitle>
          <FileText className="h-6 w-6 text-green-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{dashboardData?.stats?.proceduresDelivered || 0}</div>
          <p className="text-sm text-gray-600 mt-1">Emailed to patients</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Consents Delivered</CardTitle>
          <Shield className="h-6 w-6 text-indigo-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{dashboardData?.stats?.consentsDelivered || 0}</div>
          <p className="text-sm text-gray-600 mt-1">Emailed to patients</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Follow-Ups Delivered</CardTitle>
          <Mail className="h-6 w-6 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{dashboardData?.stats?.followupsDelivered || 0}</div>
          <p className="text-sm text-gray-600 mt-1">Emailed to patients</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Referrals Made</CardTitle>
          <UserPlus className="h-6 w-6 text-orange-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{dashboardData?.stats?.referralsMade || 0}</div>
          <p className="text-sm text-gray-600 mt-1">To specialists</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Subscription</CardTitle>
          <Activity className="h-6 w-6 text-purple-600" />
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold capitalize">
            {dashboardData?.stats?.subscriptionStatus || ''}
          </div>
          <p className="text-sm text-gray-600 mt-1">Current plan status</p>
        </CardContent>
      </Card>
    </div>

    {/* Language Demographics Card */}
    {languageDemographics.nonEnglish > 0 && (
      <Card className="mb-4" data-testid="language-demographics-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Globe className="h-5 w-5 text-teal-600" />
            Patient Language Demographics
          </CardTitle>
          <Badge variant="outline" className="text-teal-700 border-teal-300 bg-teal-50">
            {languageDemographics.nonEnglish} non-English
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Summary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">English</span>
                <span className="font-semibold">{languageDemographics.englishCount} patients</span>
              </div>
              {languageDemographics.breakdown.map(lang => (
                <div key={lang.code} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 font-medium">{lang.name}</span>
                    <span className="text-gray-600">{lang.count} ({lang.pct}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`${lang.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${Math.max(lang.pct, 3)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* Visual summary */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg p-4">
              <div className="text-5xl font-bold text-teal-700">{languageDemographics.breakdown.length}</div>
              <div className="text-sm text-teal-600 mt-1 font-medium">Languages Spoken</div>
              <div className="text-xs text-gray-500 mt-2 text-center">
                Auto-translated documents are sent in each patient's preferred language
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )}
  </>
);
